import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListCourses, useCreateCourse, useUpdateCourse, useDeleteCourse,
  useListClasses, useLinkCourseClass, useUnlinkCourseClass,
  useUpdateCoursePrefs, useReorderCourses, useArchiveCourse, useGetProfile,
  getCourseDependentsCount,
} from "@workspace/api-client-react";
import {
  Plus, Settings, BookOpen, Palette, UsersRound, X, Eye, EyeOff, GripVertical,
  Archive, ArchiveRestore, AlertTriangle,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getApiErrorMessage } from "@/lib/api-error";
import { useToast } from "@/hooks/use-toast";

const PRESET_COLORS = [
  "#1a73e8", "#e53935", "#43a047", "#fb8c00", "#8e24aa",
  "#00897b", "#f4511e", "#6d4c41", "#039be5", "#546e7a",
  "#d81b60", "#00acc1",
];

interface CourseFormData {
  name: string;
  subtitle: string;
  color: string;
}

type CourseItem = {
  id: number;
  name: string;
  subtitle?: string | null;
  color: string;
  hidden: boolean;
  sortOrder?: number | null;
  archived?: boolean;
  schoolId?: number | null;
};

const BLANK: CourseFormData = { name: "", subtitle: "", color: "#1a73e8" };

function CourseModal({
  open,
  onClose,
  initial,
  courseId,
}: {
  open: boolean;
  onClose: () => void;
  initial?: CourseFormData;
  courseId?: number;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createCourse = useCreateCourse();
  const updateCourse = useUpdateCourse();

  const [form, setForm] = useState<CourseFormData>(initial ?? BLANK);

  // Reset to blank (or the edit target) each time the dialog opens, so
  // creating a second course in quick succession never pre-populates with
  // the previous course's values.
  useEffect(() => {
    if (open) setForm(initial ?? BLANK);
  }, [open, initial]);

  const isEditing = !!courseId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: "Subject name is required", variant: "destructive" });
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        subtitle: form.subtitle.trim() || null,
        color: form.color,
      };
      if (isEditing) {
        await updateCourse.mutateAsync({ courseId: courseId!, data: payload });
        toast({ title: "Subject updated" });
      } else {
        await createCourse.mutateAsync({ data: payload });
        toast({ title: "Subject created" });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
      onClose();
    } catch (err) {
      toast({
        title: isEditing ? "Couldn't update the subject" : "Couldn't create the subject",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const set = (k: keyof CourseFormData) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Subject" : "New Subject"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Subject name *</Label>
            <Input
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              placeholder="e.g. Mathematics, English, Science"
              autoFocus
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label>Subtitle / Year group</Label>
            <Input
              value={form.subtitle}
              onChange={(e) => set("subtitle")(e.target.value)}
              placeholder="e.g. Year 10 Advanced, Stage 5"
            />
          </div>

          <div className="space-y-2">
            <Label>Colour</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set("color")(c)}
                  className="w-7 h-7 rounded-full border-2 transition-all"
                  style={{
                    backgroundColor: c,
                    borderColor: form.color === c ? "#000" : "transparent",
                    transform: form.color === c ? "scale(1.2)" : "scale(1)",
                  }}
                />
              ))}
              <div className="flex items-center gap-2 ml-1">
                <Palette className="h-4 w-4 text-gray-400" />
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => set("color")(e.target.value)}
                  className="w-7 h-7 rounded cursor-pointer border border-gray-200"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createCourse.isPending || updateCourse.isPending}
             data-write-action>
              {createCourse.isPending || updateCourse.isPending
                ? "Saving…"
                : isEditing
                ? "Save Changes"
                : "Create Subject"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ManageClassesModal({
  course,
  onClose,
}: {
  course: { id: number; name: string; color: string };
  onClose: () => void;
}) {
  const { data: allClasses } = useListClasses();
  const linkClass = useLinkCourseClass();
  const unlinkClass = useUnlinkCourseClass();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedToAdd, setSelectedToAdd] = useState<string>("");

  const enrolled = (allClasses ?? []).filter((c) => c.courseIds?.includes(course.id));
  const available = (allClasses ?? []).filter((c) => !c.courseIds?.includes(course.id));

  const handleAdd = async () => {
    if (!selectedToAdd) return;
    const cls = allClasses?.find((c) => c.id === Number(selectedToAdd));
    if (!cls) return;
    try {
      await linkClass.mutateAsync({ courseId: course.id, classId: cls.id });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      toast({ title: `${cls.name} added to ${course.name}` });
      setSelectedToAdd("");
    } catch (err) {
      toast({
        title: "Couldn't add the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleRemove = async (cls: { id: number; name: string; yearLevel?: string | null }) => {
    try {
      await unlinkClass.mutateAsync({ courseId: course.id, classId: cls.id });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      toast({ title: `${cls.name} removed from ${course.name}` });
    } catch (err) {
      toast({
        title: "Couldn't remove the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ backgroundColor: course.color }}
            />
            {course.name} — Classes
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Enrolled classes ({enrolled.length})
            </p>
            {enrolled.length === 0 ? (
              <p className="text-sm text-gray-400 border-2 border-dashed rounded-lg p-4 text-center">
                No classes linked yet
              </p>
            ) : (
              <div className="border rounded-lg divide-y">
                {enrolled.map((cls) => (
                  <div key={cls.id} className="flex items-center justify-between px-3 py-2.5">
                    <div>
                      <span className="text-sm font-medium">{cls.name}</span>
                      {cls.yearLevel && (
                        <span className="ml-2 text-xs text-gray-400">Year {cls.yearLevel}</span>
                      )}
                      <span className="ml-2 text-xs text-gray-400">
                        · {cls.studentCount} students
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                      onClick={() => handleRemove(cls)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Add a class</p>
            {available.length === 0 ? (
              <p className="text-xs text-gray-400">
                All classes are already linked to this subject.
              </p>
            ) : (
              <div className="flex gap-2">
                <Select value={selectedToAdd} onValueChange={setSelectedToAdd}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a class…" />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((cls) => (
                      <SelectItem key={cls.id} value={String(cls.id)}>
                        <span>{cls.name}</span>
                        {cls.courseIds && cls.courseIds.length > 0 && (
                          <span className="ml-2 text-xs text-gray-400">(also in {cls.courseIds.length} other)</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleAdd}
                  disabled={!selectedToAdd || linkClass.isPending}
                  className="shrink-0"
                >
                  Add
                </Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CourseCard({
  course,
  enrolledCount,
  onEdit,
  onToggleHidden,
  onManageClasses,
  onArchive,
  hiddenPending,
  archivePending,
  canArchive,
}: {
  course: CourseItem;
  enrolledCount: number;
  onEdit: (c: CourseItem) => void;
  onToggleHidden: (c: CourseItem) => void;
  onManageClasses: (c: CourseItem) => void;
  onArchive: (c: CourseItem) => void;
  hiddenPending: boolean;
  archivePending: boolean;
  canArchive: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: course.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : course.hidden ? 0.6 : 1,
      }}
      className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow group relative overflow-hidden"
      data-testid={`course-card-${course.id}`}
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
        style={{ backgroundColor: course.color }}
      />
      <div className="flex items-start justify-between pl-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            {...attributes}
            {...listeners}
            className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
            aria-label="Drag to reorder"
            data-testid={`drag-handle-course-${course.id}`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${course.color}20` }}
            >
              <BookOpen className="h-5 w-5" style={{ color: course.color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-gray-900 truncate">{course.name}</h3>
                {course.hidden && (
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-gray-500">
                    Hidden
                  </Badge>
                )}
              </div>
              {course.subtitle && (
                <p className="text-xs text-gray-500 truncate">{course.subtitle}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-400 hover:text-gray-700"
            onClick={() => onToggleHidden(course)}
            disabled={hiddenPending}
            title={course.hidden ? "Unhide" : "Hide from your view"}
            data-testid={`button-toggle-hidden-course-${course.id}`}
          >
            {course.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onEdit(course)}
          >
            <Settings className="h-3.5 w-3.5 text-gray-400" />
          </Button>
          {canArchive && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 hover:text-amber-600"
              onClick={() => onArchive(course)}
              disabled={archivePending}
              title="Archive — hides from your active list. You can restore it later. Past records keep working."
              data-testid={`button-archive-course-${course.id}`}
            >
              <Archive className="h-3.5 w-3.5 text-gray-400" />
            </Button>
          )}
        </div>
      </div>

      <div className="pl-3 mt-4 flex items-center justify-between">
        <button
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          onClick={() => onManageClasses(course)}
        >
          <UsersRound className="h-3.5 w-3.5" />
          <span>
            {enrolledCount === 0
              ? "No classes linked"
              : `${enrolledCount} class${enrolledCount === 1 ? "" : "es"}`}
          </span>
        </button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs gap-1"
          onClick={() => onManageClasses(course)}
        >
          <UsersRound className="h-3 w-3" />
          Manage Classes
        </Button>
      </div>
    </div>
  );
}

export default function Courses() {
  const queryClient = useQueryClient();
  const { data: courses, isLoading } = useListCourses();
  const { data: allClasses } = useListClasses();
  const deleteCourse = useDeleteCourse();
  const updatePrefs = useUpdateCoursePrefs();
  const reorder = useReorderCourses();
  const archiveCourse = useArchiveCourse();
  // Profile drives the head-teacher check used to gate the Archive action
  // on school-owned rows. Private (schoolId == null) courses are owned by
  // the caller (the API filters by userId), so any teacher may archive.
  const { data: profile } = useGetProfile();
  const isHeadTeacher = profile?.schoolRole === "head_teacher";
  const canArchive = (course: CourseItem) => !course.schoolId || isHeadTeacher;
  const { toast } = useToast();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{
    id: number;
    form: CourseFormData;
  } | null>(null);
  const [manageClassesCourse, setManageClassesCourse] = useState<{
    id: number;
    name: string;
    color: string;
  } | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);
  // Past courses view fetches with includeArchived=1 only when the modal
  // opens, so the active courses query stays small.
  const { data: archivedListRaw, refetch: refetchArchived } = useListCourses(
    { includeArchived: "1" },
    { query: { enabled: pastOpen } },
  );

  // Defensive local filter — server already excludes archived from the
  // default response, but we hide them again here in case a cached list
  // from a previous includeArchived fetch leaks in.
  const allCourses = ((courses ?? []) as CourseItem[]).filter((c) => !c.archived);
  const hiddenCount = allCourses.filter((c) => c.hidden).length;
  const visible = showHidden ? allCourses : allCourses.filter((c) => !c.hidden);

  const archivedList = ((archivedListRaw ?? []) as CourseItem[]).filter(
    (c) => c.archived,
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Permanently-delete state. The modal opens from the Past Courses panel.
  // We fetch dependents on open; when all counts are zero we collapse to a
  // single "Yes, delete" button (no type-to-confirm).
  const [permaDeleteTarget, setPermaDeleteTarget] = useState<CourseItem | null>(null);

  const handleToggleHidden = async (course: CourseItem) => {
    try {
      await updatePrefs.mutateAsync({
        courseId: course.id,
        data: { hidden: !course.hidden },
      });
      queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
      toast({ title: course.hidden ? "Subject unhidden" : "Subject hidden from your view" });
    } catch (err) {
      toast({
        title: "Couldn't update the subject",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  // Year-end archive: removes the course from every active picker but keeps
  // historical references resolvable for records, lessons, and exports.
  // Archive is the default destructive action when a teacher clicks the
  // archive icon on a course. It is non-destructive: the row stays so
  // every diary entry, override, and record that referenced it keeps
  // resolving. The "Archived. You can restore it from Past courses."
  // toast tells the teacher how to undo it. Hard delete now lives behind
  // a separate explicit modal in the Past courses panel (Task #83).
  const handleArchive = async (course: CourseItem) => {
    try {
      await archiveCourse.mutateAsync({
        courseId: course.id,
        data: { archived: true },
      });
      queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
      if (pastOpen) refetchArchived();
      toast({
        title: "Archived. You can restore it from Archived subjects.",
      });
    } catch (err) {
      toast({
        title: "Couldn't archive the subject",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handlePermanentDelete = async (course: CourseItem) => {
    try {
      await deleteCourse.mutateAsync({ courseId: course.id });
      queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
      refetchArchived();
      setPermaDeleteTarget(null);
      toast({ title: `"${course.name}" permanently deleted` });
    } catch (err) {
      toast({
        title: "Couldn't delete the subject",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleUnarchive = async (course: CourseItem) => {
    try {
      await archiveCourse.mutateAsync({
        courseId: course.id,
        data: { archived: false },
      });
      queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
      refetchArchived();
      toast({ title: "Subject restored" });
    } catch (err) {
      toast({
        title: "Couldn't restore the subject",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visible.findIndex((c) => c.id === active.id);
    const newIndex = visible.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedVisible = arrayMove(visible, oldIndex, newIndex);
    const nextOrder = showHidden
      ? reorderedVisible
      : [...reorderedVisible, ...allCourses.filter((c) => c.hidden)];

    queryClient.setQueryData(["/api/courses"], nextOrder);

    try {
      await reorder.mutateAsync({
        data: { orderedIds: nextOrder.map((c) => c.id) },
      });
    } catch (err) {
      toast({
        title: "Couldn't save the new order",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
    }
  };

  const enrolledCountMap = useMemo(() => {
    const map = new Map<number, number>();
    for (const course of allCourses) {
      const count = (allClasses ?? []).filter((c) => c.courseIds?.includes(course.id)).length;
      map.set(course.id, count);
    }
    return map;
  }, [allCourses, allClasses]);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-8 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subjects</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Subjects you teach — assign them to timetable periods
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => setPastOpen(true)}
            data-testid="button-open-archived-courses"
          >
            <Archive className="h-3.5 w-3.5" />
            Archived subjects
          </Button>
          {hiddenCount > 0 && (
            <Button
              variant={showHidden ? "secondary" : "outline"}
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setShowHidden((v) => !v)}
              data-testid="button-toggle-show-hidden-courses"
            >
              {showHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showHidden ? "Hide hidden" : `Show hidden (${hiddenCount})`}
            </Button>
          )}
          <Button
            className="rounded-full h-12 w-12 shadow-lg shadow-primary/30"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="h-6 w-6 text-white" />
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-100 animate-pulse rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && allCourses.length === 0 && (
        <div className="py-20 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No subjects yet</h3>
          <p className="text-gray-500 mb-6 text-sm">
            Add subjects like Maths, English or Science. Then assign them to your timetable periods.
          </p>
          <Button onClick={() => setIsCreateOpen(true)}>Create your first subject</Button>
        </div>
      )}

      {!isLoading && allCourses.length > 0 && visible.length === 0 && (
        <div className="py-16 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <EyeOff className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">All your subjects are hidden. Click "Show hidden" above to bring them back.</p>
        </div>
      )}

      {visible.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visible.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {visible.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  enrolledCount={enrolledCountMap.get(course.id) ?? 0}
                  onEdit={(c) =>
                    setEditTarget({
                      id: c.id,
                      form: {
                        name: c.name,
                        subtitle: c.subtitle ?? "",
                        color: c.color,
                      },
                    })
                  }
                  onToggleHidden={handleToggleHidden}
                  onManageClasses={(c) =>
                    setManageClassesCourse({ id: c.id, name: c.name, color: c.color })
                  }
                  onArchive={handleArchive}
                  hiddenPending={updatePrefs.isPending}
                  archivePending={archiveCourse.isPending}
                  canArchive={canArchive(course)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <CourseModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      {editTarget && (
        <CourseModal
          open
          onClose={() => setEditTarget(null)}
          initial={editTarget.form}
          courseId={editTarget.id}
        />
      )}

      {manageClassesCourse && (
        <ManageClassesModal
          course={manageClassesCourse}
          onClose={() => setManageClassesCourse(null)}
        />
      )}

      <Dialog open={pastOpen} onOpenChange={setPastOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Archived subjects</DialogTitle>
          </DialogHeader>
          {archivedList.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              You haven't archived any subjects yet. Use the archive icon on a subject to move it here at the end of a year.
            </p>
          ) : (
            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto" data-testid="archived-courses-list">
              {archivedList.map((course) => (
                <div
                  key={course.id}
                  className="flex items-center justify-between px-3 py-2.5"
                  data-testid={`past-course-row-${course.id}`}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: course.color }}
                    />
                    <span className="text-sm font-medium">{course.name}</span>
                    {course.subtitle && (
                      <span className="ml-1 text-xs text-gray-400 truncate">{course.subtitle}</span>
                    )}
                  </div>
                  {canArchive(course) && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs gap-1.5 text-blue-600"
                        onClick={() => handleUnarchive(course)}
                        disabled={archiveCourse.isPending}
                        data-testid={`button-unarchive-course-${course.id}`}
                       data-write-action>
                        <ArchiveRestore className="h-3.5 w-3.5" />
                        Restore
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-gray-400 hover:text-red-600"
                        onClick={() => setPermaDeleteTarget(course)}
                        data-testid={`button-permadelete-course-${course.id}`}
                      >
                        Permanently delete…
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPastOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {permaDeleteTarget && (
        <PermanentDeleteCourseModal
          course={permaDeleteTarget}
          onClose={() => setPermaDeleteTarget(null)}
          onConfirm={() => handlePermanentDelete(permaDeleteTarget)}
          pending={deleteCourse.isPending}
        />
      )}
    </div>
  );
}

// Permanent-delete modal (Task #83). Fetches dependents on open and renders
// the type-to-confirm flow only when at least one dependent row would be
// orphaned. Zero-dependent courses get a single "Yes, delete" button so the
// teacher doesn't have to type the name for an obviously safe deletion.
function PermanentDeleteCourseModal({
  course,
  onClose,
  onConfirm,
  pending,
}: {
  course: CourseItem;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  const [counts, setCounts] = useState<{
    classes: number;
    diaryEntries: number;
    dailyOverrideSlots: number;
    records: number;
  } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [typed, setTyped] = useState("");

  // Fetch on mount. The imperative call keeps the modal self-contained and
  // avoids racing with React Query's cache when the modal is reopened for
  // a different course.
  useEffect(() => {
    let cancelled = false;
    getCourseDependentsCount(course.id)
      .then((c) => {
        if (!cancelled) setCounts(c);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [course.id]);

  const total =
    (counts?.classes ?? 0) +
    (counts?.diaryEntries ?? 0) +
    (counts?.dailyOverrideSlots ?? 0) +
    (counts?.records ?? 0);
  const hasDependents = counts != null && total > 0;
  const requiresType = hasDependents;
  const typedMatches = typed.trim() === course.name;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Permanently delete "{course.name}"?
          </DialogTitle>
        </DialogHeader>

        {!counts && !loadError && (
          <p className="text-sm text-gray-500 py-4" data-testid="permadelete-loading">
            Checking what depends on this subject…
          </p>
        )}

        {loadError && (
          <p className="text-sm text-red-600 py-4">
            Couldn't load dependent counts. Close and try again.
          </p>
        )}

        {counts && !hasDependents && (
          <div className="py-2" data-testid="permadelete-no-dependents">
            <p className="text-sm text-gray-700">
              Nothing else in your account references this subject. Deleting it now is safe — it can't be undone, but no other rows will be affected.
            </p>
          </div>
        )}

        {counts && hasDependents && (
          <div className="space-y-3 py-2" data-testid="permadelete-with-dependents">
            <p className="text-sm text-gray-700">
              This will permanently delete:
            </p>
            <ul className="text-sm text-gray-800 list-disc pl-5 space-y-0.5">
              {counts.classes > 0 && (
                <li>
                  <strong>{counts.classes}</strong> class{counts.classes === 1 ? "" : "es"} linked to this subject
                </li>
              )}
              {counts.diaryEntries > 0 && (
                <li>
                  <strong>{counts.diaryEntries}</strong> diary entr{counts.diaryEntries === 1 ? "y" : "ies"}
                </li>
              )}
              {counts.dailyOverrideSlots > 0 && (
                <li>
                  <strong>{counts.dailyOverrideSlots}</strong> saved daily override{counts.dailyOverrideSlots === 1 ? "" : "s"}
                </li>
              )}
              {counts.records > 0 && (
                <li>
                  <strong>{counts.records}</strong> record{counts.records === 1 ? "" : "s"} attached to this subject
                </li>
              )}
            </ul>
            <p className="text-sm text-red-700 font-medium">
              This cannot be undone. Archiving instead keeps the subject out of pickers but preserves these references.
            </p>
            <div className="space-y-1.5 pt-1">
              <Label htmlFor="permadelete-confirm-input">
                Type <span className="font-mono">{course.name}</span> to confirm:
              </Label>
              <Input
                id="permadelete-confirm-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={course.name}
                autoFocus
                data-testid="permadelete-confirm-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={
              pending ||
              !counts ||
              loadError ||
              (requiresType && !typedMatches)
            }
            data-testid="permadelete-confirm-button"
            data-write-action
          >
            {pending ? "Deleting…" : "Yes, delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
